import { httpClient, httpGet, HttpMethod } from './http-client';
import {
  AddressAttributeResponseType,
  DispatchDestinationNodeType,
  FaultCode,
  LocationNodeData,
  NodeAttributesReponse,
  NodeData,
  PrioritySelectionObjectType,
  QueryOption,
  SiblingParentDetails,
  SiteSchemaData,
  SiteSchemaDropdown,
  StructureMergeDecisionData,
  StructureResponse,
  StructureType,
  StructureTypeResponse,
} from '../@types';
import { StructurePartListType } from '../pages/Structures/AddEditStructureLocation/PartsAccordianFragment';
import { StructureShipmentListType } from '../pages/Structures/AddEditStructureLocation/ShipmentAccordianFragment';
import { mapNumericEqualsOperatorForWire } from '../utils/filterModel';

// Query options interface for paginated structure requests - extends base QueryOption
export interface StructureQueryOptions extends QueryOption {
  excludedColumns?: string;
  /** Free-text multi-field search. api-structures matches it against node
   *  `name` OR `referenceNo1` (see /nodeTypeCategories/:cat/nodes). */
  multiSearchQuery?: string;
}

// Response type for paginated structure lists
export interface StructureListResponse {
  previousPage?: number;
  currentPage?: number;
  nextPage?: number;
  total?: number;
  limit?: number;
  data?: StructureResponse[];
}

// Structure creation response
export interface StructureCreationResponse extends StructureResponse {
  parentNodeId: string;
  nodeTypeCode: string;
}

// Priority selection response
export interface PrioritySelectionResponse {
  priority: string;
  priorityName: string;
}

// Fleet tag response
export interface FleetTagItem {
  id: number;
  name: string;
}

// Multiple service creation access response
export interface MultipleServiceCreationAccess {
  hasAccess: boolean;
}

// Child location count response - API returns an object keyed by node type name with counts, plus a Total
export type ChildLocationCount = Record<string, number>;

export const getStructureTypesByCategoryCode = (
  catagoryCode: string,
  signal?: AbortSignal
): Promise<StructureTypeResponse[]> => {
  return httpGet<StructureTypeResponse[]>(`/nodeTypeCategories/${catagoryCode}/nodeTypes`, signal);
};

export const createStructureType = (nodeType: StructureType) => {
  return httpClient('/nodeTypes', HttpMethod.Post, nodeType);
};

/** Structure / Client-Contract list filter fields backed by *numeric* (integer)
 *  columns on api-structures (node id, parent node id, child/part counts). Both
 *  the Structures list and the Client/Contract list use this endpoint. Their
 *  `equals` must go out as `=` — see mapNumericEqualsOperatorForWire. */
export const STRUCTURE_NUMERIC_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'id',
  'parentNodeId',
  'directChildCount',
  'totalChildCount',
  'goodPartCount',
  'partCount',
]);

export const getStructuresByTypeCategoryCode = (
  catagoryCode: string,
  queryOptions: StructureQueryOptions,
  signal?: AbortSignal
): Promise<StructureListResponse> => {
  const { filterModel, page, pageSize, sortModel, excludedColumns, multiSearchQuery } =
    queryOptions;
  let url = `/nodeTypeCategories/${catagoryCode}/nodes?page=${page + 1}&limit=${pageSize}&excludedColumns=${excludedColumns}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  // Free-text search is a single param the BFF/api-structures resolve into a
  // `name`/`referenceNo1` OR-match. Encode it — unlike structured filter values
  // it is raw user input that routinely contains spaces and `&`/`#`.
  if (multiSearchQuery) {
    url += `&multiSearchQuery=${encodeURIComponent(multiSearchQuery)}`;
  }

  filterModel.items?.forEach((filter) => {
    const operator = mapNumericEqualsOperatorForWire(
      filter?.field,
      filter?.operator,
      STRUCTURE_NUMERIC_FILTER_FIELDS
    );
    url += `&${filter?.field}=${operator},${filter?.value ?? ''}`;
  });

  return httpGet<StructureListResponse>(url, signal);
};

export const getStructuresByTypeCategoryCodeAndTypeCode = (
  catagoryCode: string,
  typeCode: string,
  signal?: AbortSignal
): Promise<StructureResponse[]> => {
  return httpGet<StructureResponse[]>(
    `/nodeTypeCategories/${catagoryCode}/nodes/${typeCode}`,
    signal
  );
};

export const getNodeChildrenByNodeType = (
  nodeId: string,
  nodeTypeCode: string,
  signal?: AbortSignal
): Promise<StructureResponse[]> => {
  return httpGet<StructureResponse[]>(
    `/nodes/${nodeId}/children?nodeTypeCode=${nodeTypeCode}`,
    signal
  );
};

export const getNodeChildren = (
  nodeId: string,
  includeInactiveChildren = false,
  signal?: AbortSignal
): Promise<StructureResponse[]> => {
  return httpGet<StructureResponse[]>(
    `/nodes/${nodeId}/children${includeInactiveChildren ? '?includeInactiveChildren=true' : ''}`,
    signal
  );
};

export const createStructure = (
  node: Record<string, unknown>
): Promise<StructureCreationResponse> => {
  return httpClient<StructureCreationResponse>('/nodes', HttpMethod.Post, node);
};

export const getNodeFilteredAttributesByCustomRange = (
  nodeId: string,
  nodeAttributes: string,
  serviceTypeCode: string,
  faultType?: string,
  actionTypeCode?: string,
  signal?: AbortSignal
): Promise<NodeAttributesReponse[]> => {
  let url = `/nodes/${nodeId}/filteredAttributes?nodeAttributes=${nodeAttributes}&serviceTypeCode=${serviceTypeCode}`;
  if (faultType) {
    url += `&faultType=${faultType}`;
  }
  if (actionTypeCode) {
    url += `&actionTypeCode=${actionTypeCode}`;
  }

  return httpGet<NodeAttributesReponse[]>(url, signal);
};

export const getNodeAttributesByCustomRange = (
  nodeId: string,
  nodeAttributes: string,
  visibilityBypassServiceId?: number,
  signal?: AbortSignal
): Promise<NodeAttributesReponse[]> => {
  let url = `/nodes/${nodeId}/attributes?nodeAttributes=${nodeAttributes}`;
  if (visibilityBypassServiceId) {
    url += `&visibilityBypassServiceId=${visibilityBypassServiceId}`;
  }
  return httpGet<NodeAttributesReponse[]>(url, signal);
};

export const updateStructure = (
  nodeId: string,
  node: Record<string, unknown>
): Promise<StructureCreationResponse> => {
  return httpClient<StructureCreationResponse>(`/nodes/${nodeId}`, HttpMethod.Put, node);
};

export const updateStructureBasicData = (
  nodeId: string,
  node: Record<string, unknown>
): Promise<StructureResponse> => {
  return httpClient<StructureResponse>(`/nodes/${nodeId}/basic`, HttpMethod.Put, node);
};

export const getNodeById = (
  nodeId: string,
  nodeTypeCategoryCode?: string,
  signal?: AbortSignal
): Promise<StructureResponse> => {
  let url = `/nodes/${nodeId}`;
  if (nodeTypeCategoryCode) {
    url += `?nodeTypeCategoryCode=${nodeTypeCategoryCode}`;
  }
  return httpGet<StructureResponse>(url, signal);
};

export const getNodeByName = (
  name: string,
  nodeTypeCode: string,
  parentNodeId?: number,
  signal?: AbortSignal
): Promise<StructureResponse> => {
  const parentNodeIdParam = parentNodeId ? `&parentNodeId=${parentNodeId}` : '';
  return httpGet<StructureResponse>(
    `/nodes/name/${name}/nodeTypeCode/${nodeTypeCode}?${parentNodeIdParam}`,
    signal
  );
};

export const getNodeSubTree = (
  nodeId: string,
  signal?: AbortSignal
): Promise<StructureResponse[]> => {
  return httpGet<StructureResponse[]>(`/nodes/${nodeId}/subtree`, signal);
};

export const getParentAttributesByPropertyType = (
  nodeId: string,
  propertyTypeCode: string,
  visibilityBypassServiceId?: number,
  signal?: AbortSignal
): Promise<NodeAttributesReponse[]> => {
  let url = `/nodes/${nodeId}/getparentattributes/${propertyTypeCode}`;
  if (visibilityBypassServiceId) {
    url += `?visibilityBypassServiceId=${visibilityBypassServiceId}`;
  }
  return httpGet<NodeAttributesReponse[]>(url, signal);
};

export const getPrioritySelection = (
  prioritySelectionObject: PrioritySelectionObjectType
): Promise<PrioritySelectionResponse> => {
  return httpClient<PrioritySelectionResponse>(
    '/nodes/priorityselection',
    HttpMethod.Post,
    prioritySelectionObject
  );
};

export const getSuggestgedNodesFromAllocationEngine = (
  serviceTypeCode: string,
  actionTypeCode: string,
  processTypeCode: string,
  brandId?: number,
  mainLocationId?: number,
  serviceId?: number,
  signal?: AbortSignal
): Promise<LocationNodeData[]> => {
  return httpGet<LocationNodeData[]>(
    `/preferedNodesByAllocationEngine?serviceTypeCode=${serviceTypeCode}&actionTypeCode=${actionTypeCode}&processTypeCode=${processTypeCode}&brandId=${brandId}&mainLocationId=${mainLocationId}&serviceId=${serviceId}`,
    signal
  );
};

export const getSiteSchemasByClientContract = (
  clientId: string,
  contractId: string,
  signal?: AbortSignal
): Promise<SiteSchemaDropdown[]> => {
  return httpGet<SiteSchemaDropdown[]>(
    `/nodeattributes/client/${clientId}/contract/${contractId}`,
    signal
  );
};

export const getAllPartsForStructures = (
  limit: number,
  page: number,
  nodeId: number,
  isEnableToShowChildrenParts: boolean,
  signal?: AbortSignal
): Promise<StructurePartListType> => {
  const url = `/getPartsByStructureLocationId?limit=${limit}&page=${page + 1}&locationId=${nodeId}&isEnableToShowChildrenParts=${isEnableToShowChildrenParts}`;
  return httpGet<StructurePartListType>(url, signal);
};

export const getAllSiteSchemas = (
  nodeId: number,
  signal?: AbortSignal
): Promise<SiteSchemaData[]> => {
  return httpGet<SiteSchemaData[]>(`/siteschema/nodeId/${nodeId}`, signal);
};

export const getSiteSchema = (
  siteSchemaId: number,
  signal?: AbortSignal
): Promise<SiteSchemaData> => {
  return httpGet<SiteSchemaData>(`/siteschema/${siteSchemaId}`, signal);
};

export const postSiteSchema = (data: SiteSchemaData): Promise<SiteSchemaData> => {
  return httpClient<SiteSchemaData>(
    '/siteschema',
    HttpMethod.Post,
    data as unknown as Record<string, unknown>
  );
};

export const updateSiteSchema = (data: SiteSchemaData): Promise<SiteSchemaData> => {
  return httpClient<SiteSchemaData>(
    `/siteschema/${data.id}`,
    HttpMethod.Put,
    data as unknown as Record<string, unknown>
  );
};

export const getLoggedInSiblingParentDetails = (
  returnImmediateParentNode?: boolean,
  signal?: AbortSignal
): Promise<SiblingParentDetails> => {
  let url = '/nodes/loggedinsibling/parentdetails';
  if (returnImmediateParentNode) {
    url += `?returnImmediateParentNode=${returnImmediateParentNode}`;
  }
  return httpGet<SiblingParentDetails>(url, signal);
};

export const getAllShipmentsForStructures = (
  limit: number,
  page: number,
  nodeId: number,
  isEnableToShowChildrenShipments: boolean,
  signal?: AbortSignal
): Promise<StructureShipmentListType> => {
  const url = `/getShipmentsByStructureLocationId?limit=${limit}&page=${page + 1}&locationId=${nodeId}&isEnableToShowChildrenShipments=${isEnableToShowChildrenShipments}`;
  return httpGet<StructureShipmentListType>(url, signal);
};

export const getDestinationAddressByNodeId = (
  nodeId: string,
  signal?: AbortSignal
): Promise<AddressAttributeResponseType[]> => {
  return httpGet<AddressAttributeResponseType[]>(`/nodes/getLocationAttributes/${nodeId}`, signal);
};

export const getNodeAttributesByPropertyTypeAndNodeIds = (
  propertyTypeCode: string,
  nodeIds: string,
  signal?: AbortSignal
): Promise<NodeAttributesReponse[]> => {
  const url = `/nodes/attributes/${propertyTypeCode}/getByIds?nodeIds=${nodeIds}`;
  return httpGet<NodeAttributesReponse[]>(url, signal);
};

export const getNodeAttributesByNodeId = (
  nodeId: number,
  signal?: AbortSignal
): Promise<NodeAttributesReponse[]> => {
  const url = `/nodes/${nodeId}/attributes`;
  return httpGet<NodeAttributesReponse[]>(url, signal);
};

export const getAllNodesByNodeType = (
  nodeTypeCode: string,
  signal?: AbortSignal
): Promise<NodeData[]> => {
  return httpGet<NodeData[]>(`/nodeTypes/${nodeTypeCode}/nodes`, signal);
};

export const mergeStructure = (data: StructureMergeDecisionData): Promise<StructureResponse> => {
  return httpClient<StructureResponse>(
    `/locationMerge/currentNode/${data.currentNodeId}/mergeNode/${data.mergeNodeId}`,
    HttpMethod.Put,
    data as unknown as Record<string, unknown>
  );
};

export const getDispatchDestinationSuggestions = (
  serviceId: number,
  mainLocationNodeId: number,
  subLocationNodeId: number,
  primaryAllocationNodeId: number,
  secondaryAllocationNodeId: number,
  signal?: AbortSignal
): Promise<DispatchDestinationNodeType[]> => {
  const url = `/destinationSuggestions/dispatchShipment?serviceId=${serviceId}&mainLocationNodeId=${mainLocationNodeId}&subLocationNodeId=${subLocationNodeId}&primaryAllocationNodeId=${primaryAllocationNodeId}&secondaryAllocationNodeId=${secondaryAllocationNodeId}`;
  return httpGet<DispatchDestinationNodeType[]>(url, signal);
};

export const getNodeAttributeById = (
  nodeAttributeId: number,
  signal?: AbortSignal
): Promise<FaultCode> => {
  return httpGet<FaultCode>(`/nodeattributes/${nodeAttributeId}`, signal);
};

export const getAllocationNodeParentData = (
  nodeId: number,
  signal?: AbortSignal
): Promise<NodeData> => {
  return httpGet<NodeData>(`/allocationNodeParentData/${nodeId}`, signal);
};

export const getCommonFleetTagsByPartTypeIds = (partTypeIds: number[]): Promise<FleetTagItem[]> => {
  return httpClient<FleetTagItem[]>('/getCommonFleetTagsByPartTypeIds', HttpMethod.Post, {
    partTypeIds,
  });
};

export const checkMultipleServiceCreationAccess = (
  nodeId: string,
  signal?: AbortSignal
): Promise<MultipleServiceCreationAccess> => {
  const url = `/nodes/${nodeId}/checkMultiCreationRule`;
  return httpGet<MultipleServiceCreationAccess>(url, signal);
};

export const childLocationCountGroupedByNodeType = (
  locationId: string,
  signal?: AbortSignal
): Promise<ChildLocationCount> => {
  let url = '/childLocationsOneLevelDown';

  if (locationId) {
    url += `?locationId=${locationId}`;
  }

  return httpGet<ChildLocationCount>(url, signal);
};
